import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingDialog, useSubscriptionByChannelId } from '@/features/billing';
import {
  CHANNEL_NAME_MAP,
  ChannelIconByType,
  ChannelIconWithLabel,
  useChannelById,
} from '@/features/channels';
import { Box } from '@/shared/ui';
import { Button } from '@/shared/ui/lib/Button';
import { Loader } from '@/shared/ui/Loader';
import { CustomTypography } from '@/shared/ui/Typography';

import { StyledCardBody, StyledCardHeader, StyledChannelCard } from './styles';

export const SubscriptionsTab = ({ channelId }: { channelId: string }) => {
  const [isOpen, setOpen] = useState(false);

  const { t } = useTranslation();

  const { channelByIdData, isPending } = useChannelById(channelId);
  const { data: subscriptionResponse, endDate } = useSubscriptionByChannelId(
    channelId,
    {
      refetchOnMount: 'always', // Подписка на бэкенде обновляется с задержкой, поэтому нужно запрашивать актуальные данные при заходе на таб
    }
  );

  if (!channelByIdData || isPending) {
    return <Loader containerSize="medium" />;
  }

  return (
    <StyledChannelCard>
      <StyledCardHeader>
        <ChannelIconByType type={channelByIdData.protocol} avatarUrl={channelByIdData.avatar_url} />
        <CustomTypography>
          {t(CHANNEL_NAME_MAP[channelByIdData.protocol])}
        </CustomTypography>
      </StyledCardHeader>
      <StyledCardBody
        sx={{ borderBottomLeftRadius: 20, borderBottomRightRadius: 20 }}
      >
        <Box>
          <CustomTypography variant="subtitle1" fontWeight={700} mb={1}>
            {t('channels:settings.inputs.channelName.label')}
          </CustomTypography>
          <CustomTypography color="text.secondary">
            {channelByIdData?.name}
          </CustomTypography>
        </Box>
        <Box>
          <CustomTypography variant="subtitle1" fontWeight={700} mb={1}>
            {t('billing:subscriptionExpiration')}
          </CustomTypography>
          <CustomTypography color="text.secondary">{endDate}</CustomTypography>
        </Box>

        {subscriptionResponse?.price && (
          <Box>
            <CustomTypography variant="subtitle1" fontWeight={700} mb={1}>
              {t('billing:price')}
            </CustomTypography>

            <CustomTypography color="text.secondary">
              {subscriptionResponse?.discount ?? subscriptionResponse?.price}
            </CustomTypography>
          </Box>
        )}

        {subscriptionResponse?.tariff && (
          <Box>
            <CustomTypography variant="subtitle1" fontWeight={700} mb={1}>
              {t('billing:currentPlan')}
            </CustomTypography>

            <CustomTypography color="text.secondary">
              {channelByIdData?.protocol === 'telegram_bot'
                ? 'Telegram Bot'
                : subscriptionResponse.tariff}
            </CustomTypography>
          </Box>
        )}

        <Button
          variant="contained"
          sx={{ minWidth: 160 }}
          disabled={!channelId || subscriptionResponse?.prolongation}
          color="primary"
          onClick={() => setOpen(true)}
        >
          {t('billing:extendSubscription')}
        </Button>
      </StyledCardBody>
      <BillingDialog
        key={channelByIdData?.protocol}
        open={isOpen}
        onClose={() => setOpen(false)}
        onBack={() => setOpen(false)}
        channelProtocol={channelByIdData?.protocol}
        channelId={channelId}
        isRenewing
        renderIconWithLabel={(renderLabelByProtocol) => (
          <ChannelIconWithLabel
            label={renderLabelByProtocol(channelByIdData?.protocol)}
            protocol={channelByIdData?.protocol}
          />
        )}
      />
    </StyledChannelCard>
  );
};
